// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Custom dropdown menus for the flight log selector (sidebar) and the
// dashboard flight selector. Each pair (button + panel + hidden native
// <select>) shares the same option-button markup via
// flightLogMenuOptionButton.

import { compactFlightOptionLabel } from "./flight-display-model.js";

export function flightLogMenuElements() {
  return {
    root: document.querySelector("[data-flight-menu]"),
    button: document.querySelector("#flightMenuButton"),
    buttonText: document.querySelector("#flightMenuButtonText"),
    panel: document.querySelector("#flightMenuPanel"),
    select: document.querySelector("#flightLogSelect"),
  };
}

export function setFlightLogMenuOpen(open) {
  const { root, button, panel } = flightLogMenuElements();
  if (!root || !button || !panel || button.disabled) return;
  root.classList.toggle("is-open", open);
  button.setAttribute("aria-expanded", open ? "true" : "false");
  panel.hidden = !open;
  if (open) {
    const selectedOption = panel.querySelector(".flight-menu-option.is-selected:not(:disabled)")
      ?? panel.querySelector(".flight-menu-option:not(:disabled)");
    selectedOption?.focus();
  }
}

export function syncFlightLogMenuButton() {
  const { button, buttonText, select } = flightLogMenuElements();
  if (!button || !buttonText || !select) return;
  button.disabled = select.disabled;
  const selectedOption = select.selectedOptions?.[0];
  const raw = selectedOption?.textContent?.trim() ?? "";
  buttonText.textContent = compactFlightOptionLabel(raw) || "Choose a flight";
}

export function renderFlightLogMenu() {
  const { panel, select } = flightLogMenuElements();
  if (!panel || !select) return;
  panel.innerHTML = "";

  [...select.children].forEach((child) => {
    const group = document.createElement("div");
    group.className = "flight-menu-group";

    if (child.tagName === "OPTGROUP") {
      const label = document.createElement("div");
      label.className = "flight-menu-group-label";
      label.textContent = child.label;
      group.append(label);
      [...child.children].forEach((option) => group.append(flightLogMenuOptionButton(option, select.value)));
    } else if (child.tagName === "OPTION") {
      group.append(flightLogMenuOptionButton(child, select.value));
    }

    if (group.children.length) panel.append(group);
  });

  syncFlightLogMenuButton();
}

export function flightLogMenuOptionButton(option, selectedValue) {
  const button = document.createElement("button");
  button.className = "flight-menu-option";
  button.type = "button";
  button.role = "option";
  button.dataset.flightMenuValue = option.value;
  button.disabled = option.disabled;
  button.setAttribute("aria-selected", option.value === selectedValue ? "true" : "false");
  button.classList.toggle("is-selected", option.value === selectedValue);
  button.classList.toggle("is-flagged", option.dataset.valveFlag === "true");

  const check = document.createElement("span");
  check.className = "flight-menu-check";
  check.textContent = option.value === selectedValue ? "✓" : "";

  const text = document.createElement("span");
  text.className = "flight-menu-option-text";
  text.textContent = option.textContent.trim();

  button.append(check, text);
  return button;
}

export function setupFlightLogMenu() {
  const { root, button, panel, select } = flightLogMenuElements();
  if (!root || !button || !panel || !select || root.dataset.boundFlightMenu === "true") return;

  button.addEventListener("click", () => setFlightLogMenuOpen(panel.hidden));
  panel.addEventListener("click", (event) => {
    const optionButton = event.target.closest("[data-flight-menu-value]");
    if (!optionButton || optionButton.disabled) return;
    select.value = optionButton.dataset.flightMenuValue;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    setFlightLogMenuOpen(false);
    button.focus();
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      setFlightLogMenuOpen(false);
      button.focus();
      return;
    }
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !panel.hidden) {
      event.preventDefault();
      const options = [...panel.querySelectorAll(".flight-menu-option:not(:disabled)")];
      const current = options.indexOf(document.activeElement);
      const offset = event.key === "ArrowDown" ? 1 : -1;
      options[(current + offset + options.length) % options.length]?.focus();
    }
  });
  document.addEventListener("click", (event) => {
    if (!root.contains(event.target)) setFlightLogMenuOpen(false);
  });
  root.dataset.boundFlightMenu = "true";
}

export function dashboardFlightMenuElements() {
  return {
    root: document.querySelector("[data-dashboard-flight-menu]"),
    button: document.querySelector("#dashboardFlightMenuButton"),
    buttonText: document.querySelector("#dashboardFlightMenuButtonText"),
    panel: document.querySelector("#dashboardFlightMenuPanel"),
    select: document.querySelector("#dashboardFlightSelect"),
  };
}

export function setDashboardFlightMenuOpen(open) {
  const { root, button, panel } = dashboardFlightMenuElements();
  if (!root || !button || !panel || button.disabled) return;
  root.classList.toggle("is-open", open);
  button.setAttribute("aria-expanded", open ? "true" : "false");
  panel.hidden = !open;
  if (open) {
    const selectedOption = panel.querySelector(".flight-menu-option.is-selected:not(:disabled)")
      ?? panel.querySelector(".flight-menu-option:not(:disabled)");
    selectedOption?.focus();
  }
}

export function renderDashboardFlightMenu() {
  const { button, buttonText, panel, select } = dashboardFlightMenuElements();
  if (!button || !buttonText || !panel || !select) return;
  panel.innerHTML = "";
  const group = document.createElement("div");
  group.className = "flight-menu-group";
  [...select.options].forEach((option) => group.append(flightLogMenuOptionButton(option, select.value)));
  if (group.children.length) panel.append(group);
  button.disabled = select.disabled;
  const raw = select.selectedOptions?.[0]?.textContent?.trim() ?? "";
  buttonText.textContent = compactFlightOptionLabel(raw) || "Choose a flight";
}

export function setupDashboardFlightMenu() {
  const { root, button, panel, select } = dashboardFlightMenuElements();
  if (!root || !button || !panel || !select || root.dataset.boundDashboardFlightMenu === "true") return;

  button.addEventListener("click", () => setDashboardFlightMenuOpen(panel.hidden));
  panel.addEventListener("click", (event) => {
    const optionButton = event.target.closest("[data-flight-menu-value]");
    if (!optionButton || optionButton.disabled) return;
    select.value = optionButton.dataset.flightMenuValue;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    setDashboardFlightMenuOpen(false);
    button.focus();
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      setDashboardFlightMenuOpen(false);
      button.focus();
      return;
    }
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !panel.hidden) {
      event.preventDefault();
      const options = [...panel.querySelectorAll(".flight-menu-option:not(:disabled)")];
      const current = options.indexOf(document.activeElement);
      const offset = event.key === "ArrowDown" ? 1 : -1;
      options[(current + offset + options.length) % options.length]?.focus();
    }
  });
  document.addEventListener("click", (event) => {
    if (!root.contains(event.target)) setDashboardFlightMenuOpen(false);
  });
  root.dataset.boundDashboardFlightMenu = "true";
}
